/**
 * scroll-update-content
 *
 * Captures wheel / touch distance while pinned to advance a virtual progress
 * track. Crossing a phase boundary triggers a timed handoff animation that
 * always plays to completion, so content never rests mid-transition.
 * No synthetic document-height scroll track is created.
 */

(() => {
const DIVIDER_RE = /rich-content-divider:(all|desktop_only|mobile_only)/;

/** Timed handoff once a phase boundary is crossed. */
const PHASE_TRANSITION_MS = 600;

/**
 * @returns {number}
 */
function detectStickyTopOffset() {
  const rootStyle = getComputedStyle(document.documentElement);
  const stickyHeaderEnabled = Number(
    rootStyle.getPropertyValue('--sticky-header-enabled').trim()
  );
  const stickyAnnouncementEnabled = Number(
    rootStyle.getPropertyValue('--sticky-announcement-bar-enabled').trim()
  );
  const headerHeight = parseFloat(rootStyle.getPropertyValue('--header-height'));
  const announcementHeight = parseFloat(
    rootStyle.getPropertyValue('--announcement-bar-height')
  );
  const tokenOffset =
    (stickyHeaderEnabled ? headerHeight || 0 : 0) +
    (stickyAnnouncementEnabled ? announcementHeight || 0 : 0);

  if (tokenOffset > 0) {
    return Math.round(tokenOffset);
  }

  const stickySelectors = [
    '.shopify-section-header',
    '#shopify-section-header',
    '.header',
    '.announcement-bar',
    '[data-section-type="announcement-bar"]',
  ];

  let maxBottom = 0;
  stickySelectors.forEach((selector) => {
    document.querySelectorAll(selector).forEach((el) => {
      const style = getComputedStyle(el);
      if (style.position !== 'fixed' && style.position !== 'sticky') return;
      const rect = el.getBoundingClientRect();
      if (rect.top > 1) return;
      if (rect.bottom > maxBottom && rect.height < window.innerHeight * 0.5) {
        maxBottom = rect.bottom;
      }
    });
  });

  return Math.max(0, Math.round(maxBottom));
}

/**
 * @returns {boolean}
 */
function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * @param {Node} node
 * @returns {'all'|'desktop_only'|'mobile_only'|null}
 */
function getDividerMode(node) {
  if (node.nodeType === Node.COMMENT_NODE) {
    const match = DIVIDER_RE.exec(node.textContent || '');
    return match ? match[1] : null;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return null;

  for (const child of node.childNodes) {
    if (child.nodeType === Node.COMMENT_NODE) {
      const match = DIVIDER_RE.exec(child.textContent || '');
      if (match) return match[1];
    }
  }

  return null;
}

/**
 * @param {'all'|'desktop_only'|'mobile_only'} mode
 * @param {'pc'|'mo'} bp
 * @returns {boolean}
 */
function shouldSplitForBreakpoint(mode, bp) {
  if (mode === 'all') return true;
  if (bp === 'pc') return mode === 'desktop_only';
  return mode === 'mobile_only';
}

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

/**
 * Smooths scroll progress with zero velocity at both endpoints.
 *
 * @param {number} value
 * @returns {number}
 */
function easeInOutCubic(value) {
  const progress = clamp(value, 0, 1);
  return progress < 0.5
    ? 4 * progress ** 3
    : 1 - ((-2 * progress + 2) ** 3) / 2;
}

class ScrollUpdateContent extends HTMLElement {
  connectedCallback() {
    this.classList.remove('is-ready');
    this._phases = [];
    this._sourceNodes = null;
    this._raf = 0;
    this._virtualProgress = 0;
    this._activeIndex = 0;
    this._isAnimating = false;
    this._animRaf = 0;
    this._touchY = null;
    this._isReverseFlow = false;
    this._isReverseReady = false;
    this._lastTriggerTop = null;
    this._onScroll = () => this.#scheduleUpdate();
    this._onWheel = (event) => this.#onWheel(event);
    this._onTouchStart = (event) => this.#onTouchStart(event);
    this._onTouchMove = (event) => this.#onTouchMove(event);
    this._onTouchEnd = () => {
      this._touchY = null;
    };
    this._onResize = () => {
      this.#applyStickyTop();
      this.#syncPhasesHeight();
      this.#scheduleUpdate();
    };
    this._onMq = () => this.#relayout();
    this._headerResizeObserver = null;

    this.#snapshotSourceNodes();

    if (window.Shopify?.designMode) {
      this.#showBlocksSource();
      return;
    }

    const mobileMax = Number(this.dataset.mobileMax || 767);
    this._mq = window.matchMedia(`(max-width: ${mobileMax}px)`);
    this._mq.addEventListener('change', this._onMq);

    this.#relayout();
    this.#observeStickyChrome();

    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onResize, { passive: true });
    window.addEventListener('wheel', this._onWheel, { passive: false });
    window.addEventListener('touchstart', this._onTouchStart, { passive: true });
    window.addEventListener('touchmove', this._onTouchMove, { passive: false });
    window.addEventListener('touchend', this._onTouchEnd, { passive: true });
    window.addEventListener('touchcancel', this._onTouchEnd, { passive: true });
  }

  disconnectedCallback() {
    this._mq?.removeEventListener('change', this._onMq);
    this._headerResizeObserver?.disconnect();
    this._headerResizeObserver = null;
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('wheel', this._onWheel);
    window.removeEventListener('touchstart', this._onTouchStart);
    window.removeEventListener('touchmove', this._onTouchMove);
    window.removeEventListener('touchend', this._onTouchEnd);
    window.removeEventListener('touchcancel', this._onTouchEnd);
    if (this._raf) cancelAnimationFrame(this._raf);
    if (this._animRaf) cancelAnimationFrame(this._animRaf);
    this.#setPinned(false);
    this.#restoreSourceNodes();
  }

  #relayout() {
    const mobileMax = Number(this.dataset.mobileMax || 767);
    const bp = window.matchMedia(`(max-width: ${mobileMax}px)`).matches ? 'mo' : 'pc';
    this.#layoutPhases(bp);
    this.#applyStickyTop();
    this.#syncPhasesHeight();
    this.#applyPhaseVisuals(this._activeIndex);
    this.classList.add('is-ready');
    this.#scheduleUpdate();
  }

  #snapshotSourceNodes() {
    const blocksRoot = this.querySelector('[data-suc-blocks]');
    if (!blocksRoot) {
      this._sourceNodes = [];
      return;
    }
    this._sourceNodes = Array.from(blocksRoot.childNodes).filter((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) return true;
      if (node.nodeType === Node.COMMENT_NODE) return DIVIDER_RE.test(node.textContent || '');
      return false;
    });
  }

  #restoreSourceNodes() {
    const blocksRoot = this.querySelector('[data-suc-blocks]');
    const phasesRoot = this.querySelector('[data-suc-phases]');
    if (blocksRoot && this._sourceNodes) {
      this._sourceNodes.forEach((node) => {
        if (node.parentNode !== blocksRoot) blocksRoot.appendChild(node);
      });
      blocksRoot.hidden = false;
    }
    if (phasesRoot) {
      phasesRoot.replaceChildren();
      phasesRoot.hidden = true;
    }
    this.classList.remove('is-phased');
    this.classList.remove('is-ready');
    this._phases = [];
  }

  #showBlocksSource() {
    this.#restoreSourceNodes();
    this.style.setProperty('--suc-phase-count', '1');
    this.classList.add('is-ready');
  }

  /**
   * @param {'pc'|'mo'} bp
   */
  #layoutPhases(bp) {
    const blocksRoot = this.querySelector('[data-suc-blocks]');
    const phasesRoot = this.querySelector('[data-suc-phases]');
    if (!blocksRoot || !phasesRoot || !this._sourceNodes) return;

    phasesRoot.replaceChildren();

    /** @type {Node[][]} */
    const phaseBuckets = [[]];
    this._sourceNodes.forEach((node) => {
      const mode = getDividerMode(node);
      if (mode) {
        if (shouldSplitForBreakpoint(mode, bp)) {
          phaseBuckets.push([]);
        }
        return;
      }
      phaseBuckets[phaseBuckets.length - 1].push(node);
    });

    const nonEmpty = phaseBuckets.filter((bucket) =>
      bucket.some((n) => n.nodeType === Node.ELEMENT_NODE)
    );

    if (!nonEmpty.length) {
      this.#showBlocksSource();
      return;
    }

    this._phases = nonEmpty.map((bucket, index) => {
      const phase = document.createElement('div');
      phase.className = 'scroll-update-content__phase';
      phase.dataset.phaseIndex = String(index);
      bucket.forEach((node) => phase.appendChild(node));
      phasesRoot.appendChild(phase);
      return phase;
    });

    blocksRoot.hidden = true;
    phasesRoot.hidden = false;
    phasesRoot.classList.add('is-stacked');
    this.classList.add('is-phased');
    this.style.setProperty('--suc-phase-count', String(this._phases.length));
    this._virtualProgress = 0;
    this._activeIndex = 0;
    this._isAnimating = false;
    if (this._animRaf) {
      cancelAnimationFrame(this._animRaf);
      this._animRaf = 0;
    }
    this._isReverseFlow = false;
    this._isReverseReady = false;
    this._lastTriggerTop = null;
  }

  #applyStickyTop() {
    const root = this.closest('.section-settings');
    if (!root) return;

    const stickyTop = detectStickyTopOffset();
    const rect = this.getBoundingClientRect();
    /*
      Pin to the current visual top when the section has already moved past the
      sticky line. Using only stickyTop here would yank content upward and
      overlap the previous section on PC.
    */
    const pinTop = rect.top < stickyTop ? rect.top : stickyTop;
    root.style.setProperty('--suc-sticky-top', `${Math.round(pinTop)}px`);
    root.style.setProperty('--suc-shell-left', `${rect.left}px`);
    root.style.setProperty('--suc-shell-width', `${rect.width}px`);
  }

  /**
   * Theme header height is published asynchronously by <height-observer>.
   * Recompute before the user's first scroll when its measurement arrives.
   */
  #observeStickyChrome() {
    const stickyChrome = document.querySelector(
      'store-header[sticky], .shopify-section--announcement-bar'
    );
    if (!stickyChrome || !window.ResizeObserver) return;

    this._headerResizeObserver = new ResizeObserver(() => {
      this.#applyStickyTop();
      this.#scheduleUpdate();
    });
    this._headerResizeObserver.observe(stickyChrome);
  }

  /**
   * @param {boolean} isPinned
   */
  #setPinned(isPinned) {
    const root = this.closest('.section-settings');
    if (!root) return;

    const wasPinned = root.classList.contains('is-scroll-update-pinned');
    if (isPinned && !wasPinned) this.#applyStickyTop();
    root.classList.toggle('is-scroll-update-pinned', isPinned);
  }

  #syncPhasesHeight() {
    const phasesRoot = this.querySelector('[data-suc-phases]');
    const phases = this._phases;
    if (!phasesRoot || !phases.length) return;

    let max = 0;
    phases.forEach((el) => {
      const prev = {
        position: el.style.position,
        visibility: el.style.visibility,
        opacity: el.style.opacity,
      };
      el.style.position = 'relative';
      el.style.visibility = 'hidden';
      el.style.opacity = '0';
      max = Math.max(max, el.offsetHeight);
      el.style.position = prev.position;
      el.style.visibility = prev.visibility;
      el.style.opacity = prev.opacity;
    });

    if (max > 0) {
      phasesRoot.style.minHeight = `${max}px`;
      phasesRoot.style.setProperty('--suc-phases-min-height', `${max}px`);
    }
  }

  #scheduleUpdate() {
    if (this._raf) return;
    this._raf = requestAnimationFrame(() => {
      this._raf = 0;
      this.#onDocumentScroll();
    });
  }

  /**
   * Section overlaps the sticky line and can own forward virtual scrolling.
   * @returns {boolean}
   */
  #isAtStickyPosition() {
    const stickyTop = detectStickyTopOffset();
    const rect = this.getBoundingClientRect();
    return rect.top <= stickyTop && rect.bottom > stickyTop;
  }

  /**
   * @returns {number}
   */
  #getVirtualScrollDistance() {
    const phaseVh = Number(this.dataset.phaseScrollVh || 12);
    return Math.max(
      1,
      (Math.max(phaseVh, 1) / 100) * window.innerHeight * Math.max(this._phases.length, 1)
    );
  }

  /**
   * @param {number} deltaY
   * @returns {boolean}
   */
  #consumeVirtualScroll(deltaY) {
    if (this._phases.length < 2 || !Number.isFinite(deltaY) || deltaY === 0) {
      return false;
    }

    const isForward = deltaY > 0;
    if (isForward && this._virtualProgress >= 1) {
      this._isReverseFlow = false;
      this.#setPinned(false);
      return false;
    }
    if (!isForward && this._virtualProgress <= 0) {
      this._isReverseFlow = false;
      this.#setPinned(false);
      return false;
    }

    const isStartingReverse = !isForward && this._virtualProgress >= 1;
    const canStartForward = this.#isAtStickyPosition();

    /*
      On the return journey, the component can overlap the sticky line while
      only its lower edge is visible. Reverse progress must wait until its top
      has naturally crossed back to that line.
    */
    if (
      (isStartingReverse && !this._isReverseReady) ||
      (!this._isReverseFlow && !isStartingReverse && !canStartForward)
    ) {
      return false;
    }

    if (isStartingReverse) {
      this._isReverseFlow = true;
    }

    const nextProgress = clamp(
      this._virtualProgress + deltaY / this.#getVirtualScrollDistance(),
      0,
      1
    );
    this._virtualProgress = nextProgress;
    if (nextProgress >= 1 && this._activeIndex >= this._phases.length - 1 && !this._isAnimating) {
      /*
        Final phase is settled. Release fixed layers in this same input event
        so the next document-scroll frame moves this section and its successor
        together instead of letting the successor pass underneath it.
      */
      this._isReverseFlow = false;
      this.#setPinned(false);
    } else if (!this._isReverseFlow && !this._isAnimating) {
      /*
        Keep pin asserted while forward virtual scroll is active, but never
        toggle it during an in-flight phase handoff.
      */
      this.#setPinned(true);
    }
    this.#syncPhaseFromProgress();
    return true;
  }

  /**
   * @param {WheelEvent} event
   */
  #onWheel(event) {
    if (prefersReducedMotion()) return;
    const deltaMultiplier = event.deltaMode === WheelEvent.DOM_DELTA_LINE
      ? 16
      : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
        ? window.innerHeight
        : 1;
    if (this.#consumeVirtualScroll(event.deltaY * deltaMultiplier)) {
      event.preventDefault();
    }
  }

  /**
   * @param {TouchEvent} event
   */
  #onTouchStart(event) {
    this._touchY = event.touches[0]?.clientY ?? null;
  }

  /**
   * @param {TouchEvent} event
   */
  #onTouchMove(event) {
    const currentY = event.touches[0]?.clientY;
    if (this._touchY === null || currentY === undefined) return;

    const deltaY = this._touchY - currentY;
    this._touchY = currentY;
    if (!prefersReducedMotion() && this.#consumeVirtualScroll(deltaY)) {
      event.preventDefault();
    }
  }

  /**
   * Maps virtual progress onto a settled phase index. Each phase owns an equal
   * slice of the track; crossing into the next slice is the trigger point.
   * @returns {number}
   */
  #getDesiredIndexFromProgress() {
    const n = this._phases.length;
    if (n <= 1) return 0;
    if (this._virtualProgress >= 1) return n - 1;
    /*
      Trigger after ~70% of a phase slice so a typical mobile flick (~80–120px)
      crosses the boundary in one gesture at 12–15vh phase distance.
    */
    const triggerRatio = 0.7;
    return clamp(Math.floor((this._virtualProgress * n) / triggerRatio), 0, n - 1);
  }

  /**
   * Scroll distance only decides when to fire. Visual handoff always runs as a
   * complete timed animation so content cannot rest mid-transition.
   */
  #syncPhaseFromProgress() {
    if (!this._phases.length || this._isAnimating) return;

    const desiredIndex = this.#getDesiredIndexFromProgress();
    if (desiredIndex === this._activeIndex) {
      this.#applyPhaseVisuals(this._activeIndex);
      return;
    }

    const step = desiredIndex > this._activeIndex ? 1 : -1;
    this.#animateToPhase(this._activeIndex, this._activeIndex + step);
  }

  /**
   * @param {number} fromIndex
   * @param {number} toIndex
   */
  #animateToPhase(fromIndex, toIndex) {
    if (this._animRaf) cancelAnimationFrame(this._animRaf);

    if (prefersReducedMotion()) {
      this._activeIndex = toIndex;
      this._isAnimating = false;
      this.#applyPhaseVisuals(toIndex);
      this.#onPhaseSettled();
      return;
    }

    this._isAnimating = true;
    if (toIndex < fromIndex) this._isReverseFlow = true;
    /*
      Do not toggle pin here. Pin is owned by scroll alignment / completion so
      phase handoffs never flash between fixed and in-flow positions.
    */

    const startedAt = performance.now();
    const tick = (now) => {
      const t = clamp((now - startedAt) / PHASE_TRANSITION_MS, 0, 1);
      this.#applyPhaseHandoff(fromIndex, toIndex, easeInOutCubic(t));

      if (t < 1) {
        this._animRaf = requestAnimationFrame(tick);
        return;
      }

      this._animRaf = 0;
      this._activeIndex = toIndex;
      this._isAnimating = false;
      this.#applyPhaseVisuals(toIndex);
      this.#onPhaseSettled();
    };

    this._animRaf = requestAnimationFrame(tick);
  }

  #onPhaseSettled() {
    const n = this._phases.length;

    if (this._activeIndex >= n - 1) {
      /*
        Last phase handoff finished: park progress at the end. Release pin only
        after the timed animation completed so the shell does not jump mid-swap.
      */
      this._virtualProgress = 1;
      this._isReverseFlow = false;
      this.#setPinned(false);
      return;
    }

    if (this._activeIndex <= 0) {
      this._virtualProgress = 0;
      this._isReverseFlow = false;
      this.#setPinned(this.#isAtStickyPosition());
      return;
    }

    /*
      Middle phases park at the slice center so a released finger cannot leave
      progress resting on a boundary and immediately re-trigger. Keep whatever
      pin state the scroll alignment already established.
    */
    this._virtualProgress = (this._activeIndex + 0.5) / n;

    if (this._isReverseFlow) {
      this.#setPinned(false);
    }

    /* Catch up if more distance was consumed during the animation. */
    this.#syncPhaseFromProgress();
  }

  /**
   * Keeps reverse-ready gating in sync with normal document scrolling.
   */
  #onDocumentScroll() {
    if (!this._phases.length) return;
    if (this._phases.length < 2) {
      this.#setPinned(false);
      this.#applyPhaseVisuals(0);
      return;
    }

    const stickyTop = detectStickyTopOffset();
    const triggerTop = this.getBoundingClientRect().top;
    if (this._activeIndex >= this._phases.length - 1 && this._virtualProgress >= 1) {
      /*
        The reverse gate opens only after normal page scrolling has brought
        the component's own top back across the sticky line. This excludes
        the earlier moment where just the component's bottom becomes visible.
      */
      if (this._lastTriggerTop !== null && this._lastTriggerTop < stickyTop && triggerTop >= stickyTop) {
        this._isReverseReady = true;
      } else if (triggerTop < stickyTop) {
        this._isReverseReady = false;
      }
    } else {
      this._isReverseReady = false;
    }
    this._lastTriggerTop = triggerTop;

    /*
      While a phase handoff is playing, freeze pin state so fixed layers are
      not added/removed in the middle of the animation.
    */
    if (this._isAnimating) return;

    if (this._isReverseFlow) {
      this.#setPinned(false);
    } else if (!(this._activeIndex >= this._phases.length - 1 && this._virtualProgress >= 1)) {
      this.#setPinned(this.#isAtStickyPosition());
    }
  }

  /**
   * @returns {number}
   */
  #getTransitionOffset() {
    return parseFloat(
      getComputedStyle(this).getPropertyValue('--suc-phase-transition-offset')
    ) || 32;
  }

  /**
   * @param {number} activeIndex
   */
  #applyPhaseVisuals(activeIndex) {
    const transitionOffset = this.#getTransitionOffset();
    this._phases.forEach((el, i) => {
      const active = i === activeIndex;
      el.style.opacity = active ? '1' : '0';
      const translateY = i < activeIndex
        ? -transitionOffset
        : i > activeIndex
          ? transitionOffset
          : 0;
      el.style.transform = `translateY(${translateY}px)`;
      el.classList.toggle('is-active', active);
    });
  }

  /**
   * Timed handoff between two adjacent phases.
   *
   * @param {number} fromIndex
   * @param {number} toIndex
   * @param {number} progress 0..1 eased
   */
  #applyPhaseHandoff(fromIndex, toIndex, progress) {
    const transitionOffset = this.#getTransitionOffset();
    const isForward = toIndex > fromIndex;
    const leaveT = clamp(progress / 0.85, 0, 1);
    const enterT = clamp((progress - 0.3) / 0.7, 0, 1);

    this._phases.forEach((el, index) => {
      let opacity = 0;
      let translateY = transitionOffset;

      if (index === fromIndex) {
        opacity = 1 - leaveT;
        translateY = isForward
          ? -transitionOffset * leaveT
          : transitionOffset * leaveT;
      } else if (index === toIndex) {
        opacity = enterT;
        translateY = isForward
          ? transitionOffset * (1 - enterT)
          : -transitionOffset * (1 - enterT);
      } else if (index < Math.min(fromIndex, toIndex)) {
        opacity = 0;
        translateY = -transitionOffset;
      } else {
        opacity = 0;
        translateY = transitionOffset;
      }

      el.style.opacity = String(opacity);
      el.style.transform = `translateY(${translateY}px)`;
      el.classList.toggle('is-active', opacity >= 0.5);
    });
  }
}

if (!customElements.get('scroll-update-content')) {
  customElements.define('scroll-update-content', ScrollUpdateContent);
}
})();
